import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { 
  Send, Users, Clock, CheckCircle, XCircle, Loader2, Save, Trash2,
  Radio, ChevronDown, ChevronUp, Menu, Bold, Italic, Strikethrough, Code, Eye, EyeOff, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { FaWhatsapp, FaInstagram, FaFacebookMessenger } from 'react-icons/fa';

const BroadcastPage = () => {
  const { user, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [message, setMessage] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [eligible, setEligible] = useState(null);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({ auto_enabled: false, auto_template_id: null, max_per_customer_per_day: 1, auto_delay_hours: 23 });
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  const [showHistory, setShowHistory] = useState(false);
  const settingsTimerRef = useRef(null);

  useEffect(() => {
    fetchEligible();
    fetchTemplates();
    fetchHistory();
    fetchSettings();
  }, []);

  useEffect(() => { fetchEligible(); }, [channelFilter]);

  const fetchEligible = async () => {
    try {
      const res = await axios.get(`${API}/broadcast/eligible?channel=${channelFilter}`, { headers });
      setEligible(res.data);
    } catch (err) {}
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/broadcast/templates`, { headers });
      setTemplates(res.data.templates || []);
    } catch (err) {}
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/broadcast/history`, { headers });
      setHistory(res.data.broadcasts || []);
    } catch (err) {}
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/broadcast/settings`, { headers });
      setSettings(res.data.settings || {});
    } catch (err) {}
  };

  // Auto-save settings with debounce
  const autoSaveSettings = useCallback((newSettings) => {
    if (settingsTimerRef.current) clearTimeout(settingsTimerRef.current);
    settingsTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/broadcast/settings`, newSettings, { headers });
        toast.success('Pengaturan disimpan', { duration: 1500 });
      } catch (err) { toast.error('Gagal menyimpan pengaturan'); }
    }, 800);
  }, [token]);

  const updateSettings = (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    autoSaveSettings(newSettings);
  };

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Tulis pesan broadcast dulu'); return; }
    if (!eligible?.total) { toast.error('Tidak ada customer yang eligible'); return; }
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    setSending(true);
    try {
      const res = await axios.post(`${API}/broadcast/send`, {
        message: message.trim(),
        channel_filter: channelFilter
      }, { headers });
      toast.success(res.data.message || `Broadcast terkirim ke ${res.data.sent} customer`);
      if (res.data.skipped > 0) {
        toast.warning(`${res.data.skipped} customer di-skip (sudah menerima broadcast hari ini)`, { duration: 5000 });
      }
      setMessage('');
      fetchHistory();
      fetchEligible();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim() || !message.trim()) { toast.error('Isi nama template dan pesan'); return; }
    try {
      await axios.post(`${API}/broadcast/templates`, { name: newTemplateName, message: message.trim() }, { headers });
      toast.success('Template disimpan');
      setNewTemplateName('');
      fetchTemplates();
    } catch (err) { toast.error('Gagal menyimpan template'); }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/broadcast/templates/${id}`, { headers });
      fetchTemplates();
    } catch (err) {}
  };

  const channelIcon = (ch) => {
    if (ch === 'whatsapp') return <FaWhatsapp className="w-3.5 h-3.5 text-green-500" />;
    if (ch === 'instagram') return <FaInstagram className="w-3.5 h-3.5 text-pink-500" />;
    if (ch === 'messenger') return <FaFacebookMessenger className="w-3.5 h-3.5 text-blue-500" />;
    return <Radio className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />;
  };

  // Insert formatting markers around selected text or at cursor
  const insertFormat = useCallback((prefix, suffix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = message.substring(start, end);
    const before = message.substring(0, start);
    const after = message.substring(end);
    
    if (selected) {
      // Wrap selected text
      setMessage(before + prefix + selected + suffix + after);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, end + prefix.length);
      }, 0);
    } else {
      // Insert markers and place cursor between them
      setMessage(before + prefix + suffix + after);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + prefix.length, start + prefix.length);
      }, 0);
    }
  }, [message]);

  // Parse WhatsApp formatting to HTML for preview
  const parseWAFormat = useCallback((text) => {
    if (!text) return '';
    let html = text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_]+)_/g, '<em>$1</em>')
      .replace(/~([^~]+)~/g, '<del>$1</del>')
      .replace(/```([^`]+)```/g, '<code class="bg-slate-200 px-1 rounded text-sm font-mono">$1</code>');
    return html.replace(/\n/g, '<br/>');
  }, []);

  // Strip formatting for plain text preview (Messenger/IG)
  const stripFormat = useCallback((text) => {
    if (!text) return '';
    return text
      .replace(/\*([^*]+)\*/g, '$1')
      .replace(/_([^_]+)_/g, '$1')
      .replace(/~([^~]+)~/g, '$1')
      .replace(/```([^`]+)```/g, '$1');
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      
      <div className="lg:ml-64">
        {/* Header */}
        <div className="bg-white dark:bg-zinc-900 border-b border-slate-200 dark:border-zinc-800 px-4 lg:px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:bg-zinc-800" data-testid="sidebar-toggle">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                <Radio className="w-5 h-5 text-indigo-600 dark:text-indigo-400" /> Broadcast
              </h1>
              <p className="text-xs text-slate-500 dark:text-zinc-400">Kirim pesan ke semua customer dalam 24 jam window</p>
            </div>
          </div>
        </div>

        <main className="p-4 lg:p-6 space-y-4">
          
          {/* ===== COMPOSE + RECIPIENTS ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Message Form */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4" data-testid="broadcast-compose">
                <Label className="mb-2 block text-sm font-medium">Pesan Broadcast</Label>
                
                {/* Formatting Toolbar */}
                <div className="flex items-center gap-1 mb-2 pb-2 border-b border-slate-100 dark:border-zinc-800">
                  <button
                    onClick={() => insertFormat('*', '*')}
                    className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 transition-colors dark:bg-zinc-800"
                    title="Bold (*text*)"
                    data-testid="format-bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => insertFormat('_', '_')}
                    className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 transition-colors dark:bg-zinc-800"
                    title="Italic (_text_)"
                    data-testid="format-italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => insertFormat('~', '~')}
                    className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 transition-colors dark:bg-zinc-800"
                    title="Strikethrough (~text~)"
                    data-testid="format-strike"
                  >
                    <Strikethrough className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => insertFormat('```', '```')}
                    className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 transition-colors dark:bg-zinc-800"
                    title="Monospace (```text```)"
                    data-testid="format-code"
                  >
                    <Code className="w-4 h-4" />
                  </button>
                  
                  <div className="w-px h-5 bg-slate-200 dark:bg-zinc-800 mx-1" />
                  
                  <button
                    onClick={() => setShowPreview(!showPreview)}
                    className={`p-1.5 rounded-md transition-colors flex items-center gap-1 ${showPreview ? 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-600' : 'hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300'}`}
                    title="Preview"
                    data-testid="format-preview"
                  >
                    {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    <span className="text-xs font-medium hidden sm:inline">Preview</span>
                  </button>
                  
                  <div className="ml-auto flex items-center gap-1 text-[10px] text-slate-500 dark:text-zinc-400">
                    <Info className="w-3 h-3" />
                    <span className="hidden sm:inline">Formatting hanya berlaku di WhatsApp</span>
                    <span className="sm:hidden">WA only</span>
                  </div>
                </div>

                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Tulis pesan broadcast Anda di sini... Gunakan *bold*, _italic_, ~coret~"
                  rows={5}
                  className={showPreview ? 'hidden' : 'mb-3'}
                  data-testid="broadcast-message"
                />

                {/* Preview Panel */}
                {showPreview && (
                  <div className="mb-3 space-y-2">
                    {/* WhatsApp Preview */}
                    <div className="rounded-lg border border-green-200 dark:border-green-800 overflow-hidden">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
                        <FaWhatsapp className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-medium text-green-700 dark:text-green-400">WhatsApp</span>
                      </div>
                      <div className="p-3 bg-[#e5ddd5] dark:bg-zinc-900 min-h-[80px]">
                        <div className="max-w-[75%] bg-[#dcf8c6] dark:bg-green-900/40 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-zinc-200 shadow-sm">
                          {message ? (
                            <div dangerouslySetInnerHTML={{ __html: parseWAFormat(message) }} />
                          ) : (
                            <span className="text-slate-500 italic dark:text-zinc-400">Tulis pesan dulu...</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Messenger/IG Preview */}
                    <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 overflow-hidden">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-200 dark:border-indigo-800">
                        <FaFacebookMessenger className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-400">Messenger / Instagram</span>
                        <span className="text-[10px] text-blue-400 ml-1">(plain text)</span>
                      </div>
                      <div className="p-3 bg-white dark:bg-zinc-900 min-h-[60px]">
                        <div className="max-w-[75%] bg-indigo-500 rounded-xl px-3 py-2 text-sm text-white shadow-sm">
                          {message ? (
                            <span style={{ whiteSpace: 'pre-wrap' }}>{stripFormat(message)}</span>
                          ) : (
                            <span className="text-blue-200 italic">Tulis pesan dulu...</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-slate-500 dark:text-zinc-400">{message.length} karakter</span>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleSend}
                      disabled={sending || !message.trim() || !eligible?.total}
                      className="bg-indigo-600 hover:bg-indigo-700 gap-1.5 w-full sm:w-auto"
                      data-testid="broadcast-send"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Kirim ke {eligible?.total || 0} Customer
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Recipients Panel */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4" data-testid="broadcast-recipients">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-zinc-300">Recipients</h3>
                  <Select value={channelFilter} onValueChange={setChannelFilter}>
                    <SelectTrigger className="w-28 h-8 text-xs" data-testid="broadcast-channel-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="messenger">Messenger</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {eligible?.by_channel && (
                  <div className="space-y-2 mb-4">
                    {Object.entries(eligible.by_channel).map(([ch, count]) => (
                      <div key={ch} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">{channelIcon(ch)}<span className="capitalize">{ch}</span></div>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t border-slate-100 dark:border-zinc-800 pt-3 max-h-48 overflow-y-auto space-y-2">
                  {eligible?.recipients?.slice(0, 20).map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        {channelIcon(r.channel)}
                        <span className="truncate text-slate-700 dark:text-zinc-300">{r.customer_name || 'Customer'}</span>
                      </div>
                      <span className="text-slate-500 shrink-0 dark:text-zinc-400">{r.hours_remaining}h</span>
                    </div>
                  ))}
                  {(eligible?.total || 0) > 20 && (
                    <p className="text-xs text-slate-500 text-center dark:text-zinc-400">+{eligible.total - 20} more</p>
                  )}
                </div>
              </div>

              <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 p-3">
                <p className="text-xs text-indigo-700 dark:text-indigo-300">
                  <strong>Gratis!</strong> Broadcast dikirim ke customer dalam 24 jam window. Tidak ada biaya tambahan.
                </p>
              </div>
            </div>
          </div>

          {/* ===== TEMPLATES ===== */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4" data-testid="broadcast-templates">
            <h3 className="text-sm font-medium text-slate-700 dark:text-zinc-300 mb-3">Templates</h3>
            
            {/* Quick use templates */}
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {templates.map(t => (
                  <div key={t.id} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-xs group">
                    <button onClick={() => setMessage(t.message)} className="text-slate-700 dark:text-zinc-300 hover:text-indigo-600 dark:hover:text-indigo-400">{t.name}</button>
                    <button onClick={() => handleDeleteTemplate(t.id)} className="text-slate-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-1 dark:text-zinc-400">
                      <XCircle className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Save new template */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} placeholder="Nama template" className="sm:w-40" data-testid="template-name-input" />
              <Button onClick={handleSaveTemplate} size="sm" variant="outline" className="gap-1.5 shrink-0" data-testid="template-save-btn" disabled={!newTemplateName.trim() || !message.trim()}>
                <Save className="w-3.5 h-3.5" /> Simpan Pesan Sebagai Template
              </Button>
            </div>
            {templates.length === 0 && <p className="text-xs text-slate-500 mt-2 dark:text-zinc-400">Tulis pesan di atas, beri nama, lalu simpan sebagai template.</p>}
          </div>

          {/* ===== AUTO BROADCAST SETTINGS ===== */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-4" data-testid="broadcast-settings">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-slate-700 dark:text-zinc-300">Auto Broadcast</h3>
                <p className="text-xs text-slate-500 mt-0.5 dark:text-zinc-400">Otomatis kirim sebelum 24 jam window expired</p>
              </div>
              <Switch 
                checked={settings.auto_enabled} 
                onCheckedChange={v => updateSettings({ auto_enabled: v })} 
                data-testid="auto-broadcast-toggle" 
              />
            </div>

            {settings.auto_enabled && (
              <div className="space-y-3 mt-4 pt-4 border-t border-slate-100 dark:border-zinc-800">
                <div>
                  <Label className="text-xs mb-1.5 block">Template</Label>
                  <Select value={settings.auto_template_id || ''} onValueChange={v => updateSettings({ auto_template_id: v })}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Pilih template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {templates.length === 0 && <p className="text-xs text-amber-600 mt-1 dark:text-amber-400">Buat template dulu di atas</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1.5 block">Kirim pada jam ke-</Label>
                    <Select value={String(settings.auto_delay_hours || 23)} onValueChange={v => updateSettings({ auto_delay_hours: parseInt(v) })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20">20 jam</SelectItem>
                        <SelectItem value="21">21 jam</SelectItem>
                        <SelectItem value="22">22 jam</SelectItem>
                        <SelectItem value="23">23 jam</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1.5 block">Max per customer/hari</Label>
                    <Select value={String(settings.max_per_customer_per_day || 1)} onValueChange={v => updateSettings({ max_per_customer_per_day: parseInt(v) })}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1x per hari</SelectItem>
                        <SelectItem value="2">2x per hari</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ===== HISTORY (collapsible) ===== */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800" data-testid="broadcast-history">
            <button 
              onClick={() => setShowHistory(!showHistory)} 
              className="w-full flex items-center justify-between p-4 text-sm font-medium text-slate-700 dark:text-zinc-300"
              data-testid="broadcast-history-toggle"
            >
              <span>Riwayat Broadcast ({history.length})</span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showHistory && (
              <div className="border-t border-slate-100 dark:border-zinc-800 divide-y divide-slate-100 dark:divide-zinc-800 max-h-96 overflow-y-auto">
                {history.length === 0 ? (
                  <div className="p-6 text-center text-slate-500 text-sm dark:text-zinc-400">Belum ada riwayat broadcast.</div>
                ) : history.map(b => (
                  <div key={b.id} className="p-4">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-mono text-slate-500 dark:text-zinc-400">#{b.id?.slice(0, 8)}</span>
                      <span className="text-xs text-slate-500 dark:text-zinc-400">
                        {new Date(b.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-zinc-300 mb-2">{b.message?.substring(0, 120)}{b.message?.length > 120 ? '...' : ''}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {b.sent > 0 && <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle className="w-3 h-3" /> {b.sent} terkirim</span>}
                      {b.failed > 0 && <span className="flex items-center gap-1 text-red-500"><XCircle className="w-3 h-3" /> {b.failed} gagal</span>}
                      {b.skipped > 0 && <span className="flex items-center gap-1 text-amber-500"><Clock className="w-3 h-3" /> {b.skipped} di-skip</span>}
                      <span className="text-slate-500 dark:text-zinc-400">{b.total_recipients} total</span>
                    </div>
                    {b.skipped > 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                        {b.skipped} customer di-skip karena sudah menerima broadcast hari ini (maks 1x/hari)
                      </p>
                    )}
                    {b.failed > 0 && (
                      <p className="text-xs text-red-500 mt-1.5 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                        {b.failed} customer gagal — token channel mungkin expired atau customer tidak dalam 24 jam window
                      </p>
                    )}
                    {b.results?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {b.results.map((r, ri) => (
                          <div key={ri} className="flex items-center justify-between text-xs px-2 py-0.5 rounded bg-slate-50 dark:bg-zinc-800/30">
                            <span className="text-slate-600 dark:text-zinc-400 truncate">{r.customer || 'Customer'}</span>
                            <span className={`font-medium shrink-0 ml-2 ${r.status === 'sent' ? 'text-green-600' : r.status === 'skipped' ? 'text-amber-500' : 'text-red-500'} dark:text-green-400`}>
                              {r.status === 'sent' ? 'Terkirim' : r.status === 'skipped' ? 'Di-skip' : 'Gagal'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </main>

        {/* Broadcast Confirmation Modal */}
        <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
          <AlertDialogContent className="max-w-sm bg-white text-slate-900 dark:text-zinc-50 dark:bg-zinc-900">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-slate-900 dark:text-zinc-50">Kirim Broadcast</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                Kirim broadcast ke <strong className="text-slate-900 dark:text-zinc-50">{eligible?.total || 0} customer</strong>? Pesan akan dikirim ke semua customer yang eligible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-300 dark:text-zinc-200 dark:bg-zinc-800">Batal</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmSend} className="bg-indigo-600 text-white hover:bg-indigo-700">Kirim</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default BroadcastPage;
